/**
 * Shared Zod schemas for VeilBrowser
 */

import { z } from "zod";
import { ACTIVITY_ACTOR_TYPES } from "../constants/admin.ts";
import {
	BROWSER_VERSION_MAX,
	BROWSER_VERSION_MIN,
} from "../constants/index.ts";
// The tenant activity feed shows the same event actions the operator console
// searches, so it builds on that file's enum rather than restating it. One
// direction only: `admin.ts` never imports back from here.
import { ActivityEventActionSchema } from "./admin.ts";
import { CookiesSchema } from "./cookies.ts";
import {
	ArchSchema,
	OsSchema,
	ProxyTypeSchema,
	WindowsVersionSchema,
} from "./primitives.ts";
import {
	LaunchableHintSchema,
	ProfileCatalogSchema,
} from "./profile-catalog.ts";
import {
	ProfileSettingsSchema,
	refineSettingsForOs,
} from "./profile-settings.ts";
import {
	ProfileBrowserStateSchema,
	ProfileStateDownloadSchema,
} from "./profile-state.ts";
import { ProxyCheckResultSchema } from "./proxy-check.ts";

// Cross-tenant admin API. Kept in its own file: it is a separate surface with
// its own consumers, and this one is already long enough.
export * from "./admin.ts";
export * from "./cookies.ts";
export * from "./primitives.ts";
export * from "./profile-catalog.ts";
export * from "./profile-form.ts";
export * from "./profile-settings.ts";
export * from "./profile-state.ts";
export * from "./proxy-check.ts";

// ============ Common Validators ============

export const IpValidatorSchema = z
	.union([z.ipv4().describe("IPv4 address"), z.ipv6().describe("IPv6 address")])
	.describe("IP address");

export const HostnameValidatorSchema = z.hostname().describe("Hostname");

export const PortSchema = z.coerce
	.number()
	.int()
	.min(1)
	.max(65535)
	.describe("Port");

/**
 * A multi-value query parameter -> validated array.
 *
 * The data tables' faceted filters are multi-select ("show me failed and
 * cancelled"), which a single `?status=` value can't express.
 *
 * Both wire forms are accepted because the same parameter reaches the handler
 * in three shapes. Elysia's standard-schema query parser already splits a value
 * containing a comma, so `?os=win,mac` arrives as an array while `?os=win`
 * arrives as a string; Eden serialises an array argument as repeated keys, and
 * `?os=win&os=mac` arrives as an array too. Splitting a string here as well
 * keeps a hand-written URL working wherever that parser is not in front of it.
 *
 * Empty segments are dropped and an empty selection becomes `undefined`, so
 * `?status=` and a cleared filter both mean "no filter" rather than "match
 * nothing".
 */
export function csvEnum<T extends string>(item: z.ZodType<T, string>) {
	return z
		.union([z.string(), z.array(z.string())])
		.optional()
		.transform((raw) => {
			if (raw === undefined) return undefined;
			const parts = (Array.isArray(raw) ? raw : raw.split(","))
				.map((part) => part.trim())
				.filter((part) => part.length > 0);
			return parts.length > 0 ? parts : undefined;
		})
		.pipe(z.array(item).min(1).optional());
}

// ============ Core Schemas ============

/** Browser version schema with min/max constraints */
export const BrowserVersionSchema = z.coerce
	.number()
	.int()
	.min(BROWSER_VERSION_MIN)
	.max(BROWSER_VERSION_MAX)
	.describe(
		`Chrome browser version (${BROWSER_VERSION_MIN}-${BROWSER_VERSION_MAX})`,
	);

/**
 * Launch-time browser version: integer major (e.g. 147) or full version
 * string (e.g. "147.0.7727.139"). The local API resolves either form
 * to a concrete on-disk install via `version-resolver.ts`.
 */
export const LaunchBrowserVersionSchema = z
	.union([
		z.number().int().positive(),
		z.string().regex(/^\d+(\.\d+\.\d+\.\d+)?$/),
	])
	.describe(
		"Browser version: integer major (147) or full version string (147.0.7727.139)",
	);

/** Version params schema (for route params) */
export const VersionParamsSchema = z.object({
	version: BrowserVersionSchema,
});

/** OS architecture enum for profile filtering */
export const OsArchSchema = z
	.enum(["x86", "arm"])
	.describe("OS architecture type");

export const WidthSchema = z
	.number()
	.int()
	.min(1)
	.max(7680)
	.describe("Resolution Width");
export const HeightSchema = z
	.number()
	.int()
	.min(1)
	.max(4320)
	.describe("Resolution Height");

export const DevicePixelRatioSchema = z.number().describe("Device pixel ratio");

/** Custom screen dimensions object */
export const ScreenDimensionsSchema = z
	.object({
		screenWidth: WidthSchema.describe("Physical screen width"),
		screenHeight: HeightSchema.describe("Physical screen height"),
		viewportWidth: WidthSchema.optional().describe(
			"Viewport width (defaults to screenWidth)",
		),
		viewportHeight: HeightSchema.optional().describe(
			"Viewport height (defaults to screenHeight)",
		),
		colorDepth: z.number().int().optional().default(24).describe("Color depth"),
		pixelDepth: z.number().int().optional().default(24).describe("Pixel depth"),
		devicePixelRatio:
			DevicePixelRatioSchema.optional().describe("Device pixel ratio"),
	})
	.describe("Custom screen dimensions configuration");

/** Screen configuration schema - supports modes or custom dimensions
 * - "real": Use real system screen
 * - "masked": Use profile masked screen (maps to "profile" in BotBrowser)
 * - object: Custom screen dimensions
 */
export const ScreenSchema = z
	.union([
		z.literal("real").describe("Use real system screen"),
		z.literal("masked").describe("Use profile masked screen"),
		ScreenDimensionsSchema,
	])
	.describe("Screen configuration: 'real', 'masked', or custom dimensions");

/** Fingerprint schema (profile selection and launch configuration) */
export const FingerprintSchema = z
	.object({
		// Required fields
		os: OsSchema.describe("Operating system (required)"),
		browserVersion: LaunchBrowserVersionSchema.describe(
			"Browser version (required) — integer major or full version string",
		),
		osVersion: z
			.string()
			.optional()
			.describe("OS version (e.g., 19.0.0, 15.0.0)"),

		// Optional fields for filtering and configuration
		userAgent: z.string().optional().describe("Custom user agent string"),
		osArch: OsArchSchema.optional().describe("OS architecture filter"),
		renderer: z
			.string()
			.optional()
			.describe("GPU renderer filter (partial match)"),
		model: z.string().optional().describe("Device model filter (Android)"),
		screen: ScreenSchema.optional().describe("Screen configuration"),
		hardwareConcurrency: z
			.number()
			.int()
			.positive()
			.optional()
			.describe("Number of CPU cores"),
	})
	.describe(
		"Browser fingerprint configuration for profile selection and launch",
	);

/** Browser launch options (Chrome flags) */
export const BrowserOptionsBaseSchema = z.object({
	headless: z
		.boolean()
		.optional()
		.default(false)
		.describe("Run browser in headless mode"),
	debugPort: z
		.number()
		.int()
		.min(1024)
		.max(65535)
		.optional()
		.describe("Chrome DevTools remote debugging port"),
	display: z
		.number()
		.int()
		.optional()
		.describe(
			"X11 display number for Xvfb (Linux only, used when headless=false). " +
				"Defaults to a round-robin Xvfb display managed by the local-api.",
		),
	args: z
		.array(z.string())
		.optional()
		.default([])
		.describe("Additional Chrome command line arguments"),
});

export const BrowserOptionsSchema = BrowserOptionsBaseSchema.refine(
	(data) => {
		// should not allow display when headless
		if (data.display && data.headless) {
			return false;
		}
		return true;
	},
	{
		message: "display parameter can only be used when headless=false",
		path: ["display"],
	},
).describe("Browser launch options");

/** Proxy schema (VeilBrowser format) */
export const ProxySchema = z
	.object({
		type: ProxyTypeSchema.default("http"),
		host: z.hostname().describe("Proxy server host"),
		port: z.number().int().positive().max(65535).describe("Proxy server port"),
		username: z
			.string()
			.optional()
			.describe("Proxy authentication username (optional)"),
		password: z
			.string()
			.optional()
			.describe("Proxy authentication password (optional)"),
	})
	.describe("Proxy server configuration");

/** Launch request schema */
export const LaunchRequestSchema = z
	.object({
		title: z
			.string()
			.min(4, "Title must be at least 4 characters")
			.describe("Session title (displayed in browser title bar)"),
		profilePath: z
			.string()
			.optional()
			.describe("Direct path to profile file or filename"),
		fingerprint: FingerprintSchema.optional().describe(
			"Fingerprint for profile selection and launch configuration",
		),
		browser: BrowserOptionsSchema.optional().describe("Browser launch options"),
		proxy: ProxySchema.optional().describe("Proxy configuration"),
	})
	.refine((data) => data.profilePath || data.fingerprint, {
		message: "Either profilePath or fingerprint required",
	})
	.describe("Browser session launch request");

/** Launch request with profile ID (fetches config from SaaS API) */
export const ProfileLaunchRequestSchema = z
	.object({
		profileId: z
			.string()
			.optional()
			.describe("Existing profile ID from SaaS API"),
		spec: z
			.lazy(() => ProfileSpecSchema)
			.optional()
			.describe("Launch by criteria — creates a profile (default one-time)"),
		browser: BrowserOptionsSchema.optional(),
		proxyOverride: ProxySchema.optional().describe(
			"Override the profile's proxy settings",
		),
	})
	.refine((d) => !!d.profileId !== !!d.spec, {
		message: "Provide exactly one of profileId or spec",
	})
	.describe("Launch browser session using a cloud profile or criteria");

export type ProfileLaunchRequest = z.infer<typeof ProfileLaunchRequestSchema>;

/** Session ID params schema */
export const SessionIdParamsSchema = z.object({
	id: z.string().describe("Session UUID"),
});

// ============ Local Profile Metadata Schemas ============

/** Profile metadata from .enc file (for local profile indexing) */
export const LocalProfileMetadataSchema = z
	.object({
		filename: z.string().describe("Profile filename"),
		fullPath: z.string().describe("Full path to the profile file"),
		hash: z.string().describe("Profile unique hash identifier"),
		os: OsSchema.describe("Operating system"),
		osVersion: z
			.string()
			.describe(
				"OS version - raw platformVersion for Windows (e.g., 19.0.0), semver for others",
			),
		windowsVersion: WindowsVersionSchema.optional().describe(
			"User-friendly Windows version (only for Windows profiles, e.g., 10, 11)",
		),
		arch: ArchSchema.describe("CPU architecture"),
		browserVersion: z.number().describe("Chrome browser version"),
		gpuName: z.string().describe("Simplified GPU name (e.g., RTX-4060)"),
		unmaskedRenderer: z.string().describe("Full WebGL renderer string"),
		model: z.string().optional().describe("Device model (Android only)"),
	})
	.describe("Local browser profile metadata");

export type LocalProfileMetadata = z.infer<typeof LocalProfileMetadataSchema>;

// ============ Session Schemas ============

/** Session response schema */
export const SessionResponseSchema = z
	.object({
		id: z.string().describe("Unique session identifier (UUID)"),
		pid: z.number().describe("Browser process ID"),
		debugPort: PortSchema.min(1024).optional().describe("CDP debugging port"),
		fingerprint: FingerprintSchema.nullable().describe(
			"Fingerprint used to launch session",
		),
		browserOptions: BrowserOptionsBaseSchema.omit({ debugPort: true })
			.optional()
			.describe("Browser launch options used for this session"),
		status: z.string().describe("Session status (running, exited, killed)"),
		startedAt: z.string().describe("ISO 8601 timestamp when session started"),
		endedAt: z
			.string()
			.nullable()
			.describe("ISO 8601 timestamp when session ended"),
		webSocketDebuggerUrl: z
			.string()
			.nullable()
			.optional()
			.describe("CDP WebSocket debugger URL for automation"),
		profileMetadata: LocalProfileMetadataSchema.describe(
			"Profile metadata for this session",
		),
	})
	.describe("Browser session information");

// ============ Response Schemas ============

/** Success response wrapper (generic) */
export const SuccessResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
	z
		.object({
			success: z.literal(true),
			data: dataSchema,
		})
		.describe("Successful API response");

/** Profiles endpoint responses */
export const ListVersionsResponseSchema = SuccessResponseSchema(
	z.object({
		versions: z.array(z.number()).describe("Available browser version numbers"),
	}),
).describe("List of available browser versions");

export const AndroidModelsQuerySchema = z
	.object({
		browserVersion: BrowserVersionSchema.describe("Browser version (required)"),
	})
	.describe("Android models query parameters");

export const AndroidModelsResponseSchema = SuccessResponseSchema(
	z.object({
		browserVersion: z.number().describe("Browser version"),
		models: z.array(z.string()).describe("Unique Android device models"),
	}),
).describe("Android models response");

/** Sessions endpoint responses */
export const SessionResponseWrapperSchema = SuccessResponseSchema(
	SessionResponseSchema,
).describe("Single session response");

/**
 * A freshly launched session, addressed through the worker's own broker.
 *
 * The URLs here are the worker's public ones, not the loopback CDP endpoint
 * Chromium bound. Every launch is registered in the cluster registry, so the
 * broker can reach it whichever door the launch came in through, and a caller
 * on another machine gets an address that resolves.
 */
export const ProfileLaunchResponseSchema = SessionResponseSchema.extend({
	launchId: z
		.string()
		.describe("Broker id for this browser; addresses it under /browsers/:id"),
	webSocketDebuggerUrl: z
		.string()
		.describe(
			"Brokered CDP WebSocket URL, carrying a per-launch control capability",
		),
	devtoolsFrontendUrl: z
		.string()
		.describe("Session viewer opened on the DevTools tab"),
	viewUrl: z.string().describe("Session viewer"),
}).describe("Launched session");

export const ProfileLaunchResponseWrapperSchema = SuccessResponseSchema(
	ProfileLaunchResponseSchema,
).describe("Profile launch response");

// ============ Pagination Schemas ============

/** Pagination query params (for API requests) */
export const PaginationQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1).describe("Page number"),
	limit: z.coerce
		.number()
		.int()
		.min(1)
		.max(2000)
		.default(50)
		.describe("Items per page"),
	// Bounded identifier rather than a free string: consumers interpolate this
	// into an ORM `orderBy` key, so it must never carry arbitrary input.
	// Endpoints that sort in the database override this with an explicit enum.
	sortBy: z
		.string()
		.regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/)
		.optional()
		.describe("Field to sort by"),
	sortOrder: z.enum(["asc", "desc"]).default("desc").describe("Sort direction"),
});

/** Generic paginated response wrapper */
export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(
	itemSchema: T,
) =>
	z.object({
		items: z.array(itemSchema).describe("Array of items"),
		total: z.number().int().min(0).describe("Total number of items"),
		page: z.number().int().min(1).describe("Current page number"),
		limit: z.number().int().min(1).describe("Items per page"),
		totalPages: z.number().int().min(0).describe("Total number of pages"),
		hasMore: z.boolean().describe("Whether there are more pages"),
	});

/** Pagination query type */
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/** Paginated response type (generic) */
export interface PaginatedResponse<T> {
	items: T[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
	hasMore: boolean;
}

/** Helper to build pagination metadata */
export function buildPaginationMeta(
	total: number,
	page: number,
	limit: number,
) {
	const totalPages = Math.ceil(total / limit);
	return {
		total,
		page,
		limit,
		totalPages,
		hasMore: page < totalPages,
	};
}

// ============ Session Query Schemas ============

/** Session status enum */
export const SessionStatusSchema = z
	.enum(["running", "exited", "killed"])
	.describe("Session status");

export type SessionStatus = z.infer<typeof SessionStatusSchema>;

/** Session query with pagination and filters */
export const SessionsQuerySchema = PaginationQuerySchema.extend({
	status: SessionStatusSchema.optional().describe("Filter by session status"),
}).describe("Session query parameters with pagination");

export type SessionsQuery = z.infer<typeof SessionsQuerySchema>;

/** Session search response with pagination */
export const SessionSearchResponseSchema = PaginatedResponseSchema(
	SessionResponseSchema,
).describe("Session search response with pagination");

export type SessionSearchResponse = z.infer<typeof SessionSearchResponseSchema>;

// ============ Organization Schemas (SaaS API) ============

/** Subscription tier enum */
export const SubscriptionTierSchema = z
	.enum(["lite", "starter", "base", "team", "advanced"])
	.describe("Subscription tier");

/**
 * Create organization request.
 *
 * There is deliberately no `subscriptionTier` here: a plan is something the
 * organization pays for, so it can only be set by the billing flow. New
 * organizations always start on the free tier.
 */
export const CreateOrganizationSchema = z
	.object({
		name: z.string().min(1).max(100).describe("Organization name"),
		logo: z.string().url().optional().describe("Logo URL"),
	})
	.describe("Create organization request");

/** Update organization request (plan changes go through billing, not here) */
export const UpdateOrganizationSchema = z
	.object({
		name: z.string().min(1).max(100).optional().describe("Organization name"),
		logo: z.string().url().optional().nullable().describe("Logo URL"),
	})
	.describe("Update organization request");

/** Invite member request */
export const InviteMemberSchema = z
	.object({
		email: z.string().email().describe("Email address to invite"),
		role: z.enum(["admin", "member"]).optional().describe("Member role"),
	})
	.describe("Invite member request");

/** Update member role request */
export const UpdateMemberRoleSchema = z
	.object({
		role: z.enum(["admin", "member"]).describe("New role"),
	})
	.describe("Update member role request");

/** Member response schema */
export const MemberResponseSchema = z
	.object({
		id: z.string(),
		userId: z.string(),
		name: z.string(),
		email: z.string(),
		image: z.string().nullable(),
		role: z.string(),
		joinedAt: z.string(),
	})
	.describe("Member response");

/** Organization ID params */
export const OrganizationIdParamsSchema = z.object({
	id: z.string().describe("Organization ID"),
});

/** Member ID params */
export const MemberIdParamsSchema = z.object({
	id: z.string().describe("Organization ID"),
	memberId: z.string().describe("Member ID"),
});

/** Invitation ID params */
export const InvitationIdParamsSchema = z.object({
	id: z.string().describe("Organization ID"),
	invitationId: z.string().describe("Invitation ID"),
});

// ============ Organization Usage History ============

/**
 * Ceiling on the daily-bucket window. The buckets are computed with an
 * aggregate over `profile_sessions`, so the window is what bounds the work;
 * without a maximum a caller could ask for the organization's entire history
 * on every dashboard render.
 */
export const USAGE_HISTORY_MAX_DAYS = 90;

/** Ceiling on how many billing periods a single request may return. */
export const USAGE_HISTORY_MAX_PERIODS = 12;

/** Usage history query parameters */
export const UsageHistoryQuerySchema = z
	.object({
		days: z.coerce
			.number()
			.int()
			.min(1)
			.max(USAGE_HISTORY_MAX_DAYS)
			.default(30)
			.describe("Length of the daily window, in days"),
		periods: z.coerce
			.number()
			.int()
			.min(1)
			.max(USAGE_HISTORY_MAX_PERIODS)
			.default(6)
			.describe("How many billing periods to return, oldest first"),
	})
	.describe("Organization usage history query parameters");

export type UsageHistoryQuery = z.infer<typeof UsageHistoryQuerySchema>;

/** One UTC day of session starts */
export const UsageHistoryDaySchema = z
	.object({
		date: z.string().describe("UTC day as YYYY-MM-DD"),
		launches: z.number().int().min(0).describe("Sessions started that day"),
	})
	.describe("Daily session-start bucket");

export type UsageHistoryDay = z.infer<typeof UsageHistoryDaySchema>;

/** One billing period of recorded usage */
export const UsageHistoryPeriodSchema = z
	.object({
		periodStart: z.string().describe("Period start, ISO 8601"),
		periodEnd: z.string().describe("Period end, ISO 8601"),
		sessionsLaunched: z.number().int().min(0),
		profilesCreated: z.number().int().min(0),
		profilesDeleted: z.number().int().min(0),
		peakProfiles: z.number().int().min(0),
		peakConcurrentSessions: z.number().int().min(0),
		isCurrent: z
			.boolean()
			.describe("Whether this period is still accumulating"),
	})
	.describe("Billing period usage");

export type UsageHistoryPeriod = z.infer<typeof UsageHistoryPeriodSchema>;

/**
 * What the organization is holding against its concurrency limit right now:
 * running sessions plus reservations taken out by launches that have been
 * authorized but not yet reported. A null limit means unlimited.
 */
export const UsageConcurrencySchema = z
	.object({
		current: z.number().int().min(0),
		runningSessions: z.number().int().min(0),
		reservedSlots: z.number().int().min(0),
		limit: z.number().int().min(0).nullable(),
	})
	.describe("Launch concurrency against the plan limit");

export type UsageConcurrency = z.infer<typeof UsageConcurrencySchema>;

/** Usage history response payload */
export const UsageHistorySchema = z
	.object({
		windowStart: z.string().describe("First day of the window, ISO 8601"),
		windowEnd: z.string().describe("End of the window, ISO 8601"),
		days: z.number().int().min(1).describe("Days covered by launchesByDay"),
		launchesByDay: z
			.array(UsageHistoryDaySchema)
			.describe("One entry per day in the window, including empty days"),
		periods: z
			.array(UsageHistoryPeriodSchema)
			.describe("Recorded billing periods, oldest first"),
		concurrency: UsageConcurrencySchema,
	})
	.describe("Organization usage history");

export type UsageHistory = z.infer<typeof UsageHistorySchema>;

// ============ Organization Activity Feed ============

/**
 * The tenant-facing activity feed unions two tables because each fact has
 * exactly one writer. `ActivityEvent` holds auth and tenant mutations;
 * `ProfileSession` has always been the permanent record of browser session
 * lifecycle, so opens and closes are read from there rather than duplicated into
 * events. `source` says which half an entry came from, and is also the filter a
 * reader uses to look at one of them alone.
 */
export const OrganizationActivitySourceSchema = z
	.enum(["event", "session"])
	.describe("Which table the entry was read from");

export type OrganizationActivitySource = z.infer<
	typeof OrganizationActivitySourceSchema
>;

/**
 * Actions synthesised from a `ProfileSession` row rather than stored anywhere.
 * A row with `endedAt` set yields both, at `startedAt` and at `endedAt`; the
 * close carries the row's `status`, since a crash and a clean stop are both
 * closes.
 */
export const SESSION_ACTIVITY_ACTIONS = [
	"session.opened",
	"session.closed",
] as const;

export type SessionActivityAction = (typeof SESSION_ACTIVITY_ACTIONS)[number];

export const SessionActivityActionSchema = z
	.enum(SESSION_ACTIVITY_ACTIONS)
	.describe("Session lifecycle action derived from ProfileSession");

/** Every action the feed can show, from either half of the union. */
export const OrganizationActivityActionSchema = z
	.union([ActivityEventActionSchema, SessionActivityActionSchema])
	.describe("Stored event action or derived session action");

export type OrganizationActivityAction = z.infer<
	typeof OrganizationActivityActionSchema
>;

/** Whether an action belongs to the `ProfileSession` half of the union. */
export function isSessionActivityAction(
	action: string,
): action is SessionActivityAction {
	return (SESSION_ACTIVITY_ACTIONS as readonly string[]).includes(action);
}

/**
 * Ceiling on one page of the feed. Lower than the tenant default of 2000
 * because the union is assembled in the API rather than by the database: it
 * reads `page * limit` rows from each half to guarantee the merged page is
 * correct, so an unbounded limit would multiply.
 */
export const ORGANIZATION_ACTIVITY_MAX_LIMIT = 100;

export const OrganizationActivityQuerySchema = z
	.object({
		page: z.coerce.number().int().min(1).default(1).describe("Page number"),
		limit: z.coerce
			.number()
			.int()
			.min(1)
			.max(ORGANIZATION_ACTIVITY_MAX_LIMIT)
			.default(25)
			.describe("Items per page"),
		sortOrder: z
			.enum(["asc", "desc"])
			.default("desc")
			.describe("Newest first by default"),
		source: csvEnum(OrganizationActivitySourceSchema).describe(
			"Comma-separated halves of the union to show",
		),
		action: csvEnum(OrganizationActivityActionSchema).describe(
			"Comma-separated actions to show. Only session actions implies source=session.",
		),
		from: z.string().datetime().optional().describe("Window start, ISO 8601"),
		to: z.string().datetime().optional().describe("Window end, ISO 8601"),
	})
	.describe("Organization activity feed query");

export type OrganizationActivityQuery = z.infer<
	typeof OrganizationActivityQuerySchema
>;

/**
 * One row of the feed, flattened so both halves render through the same cell.
 *
 * `actorLabel` is whatever the writer recorded as of the event and is never
 * joined to `user` at read time, so a renamed or deleted member still reads
 * correctly. Session entries have no actor: a worker opens them, not a person.
 */
export const OrganizationActivityEntrySchema = z
	.object({
		/**
		 * Stable and unique across the union. Event ids and session ids come from
		 * different tables and a session contributes two entries, so this is
		 * qualified rather than raw.
		 */
		id: z.string(),
		occurredAt: z.string().describe("ISO 8601"),
		source: OrganizationActivitySourceSchema,
		action: OrganizationActivityActionSchema,
		actorType: z.enum(ACTIVITY_ACTOR_TYPES).nullable(),
		actorLabel: z.string().nullable(),
		targetType: z.string().nullable(),
		targetId: z.string().nullable(),
		/** A human-readable target where one exists, e.g. the profile name. */
		targetLabel: z.string().nullable(),
		metadata: z.record(z.string(), z.unknown()).nullable(),
		/** Nulled once past `ipRetentionDays`; the entry itself survives. */
		ip: z.string().nullable(),
	})
	.describe("One organization activity entry");

export type OrganizationActivityEntry = z.infer<
	typeof OrganizationActivityEntrySchema
>;

// ============ SaaS Profile Schemas ============

/** Profile proxy configuration */
export const SaasProfileProxySchema = z
	.object({
		type: ProxyTypeSchema.default("http"),
		host: z.string().describe("Proxy server host"),
		port: z.number().int().min(1).max(65535).describe("Proxy server port"),
		username: z.string().optional().describe("Proxy username"),
		password: z.string().optional().describe("Proxy password"),
	})
	.describe("Profile proxy configuration");

/**
 * Profile spec — the single source of truth shared by web profile creation and
 * worker launch-by-criteria. `autoDelete` is NOT a field; it is derived from
 * `profileType` (one-time => auto-delete).
 *
 * The base object is exported separately because zod refuses to `.extend()` or
 * `.partial()` an object that carries refinements; every schema built from it
 * re-applies `refineSettingsForOs`.
 */
export const ProfileSpecBaseSchema = z
	.object({
		name: z.string().min(1).max(100).optional().describe("Profile name"),
		// Must match OsSchema: profile bundles ship android/ directories and the
		// worker resolves them, so omitting android here rejected launches for
		// profiles that were installed and working.
		os: OsSchema.describe("Operating system"),
		osVersion: z.string().optional().describe("OS version (e.g., 10, 11, 14)"),
		model: z
			.string()
			.max(64)
			.optional()
			.describe("Device model, Android bundles only (e.g., SM-G991B)"),
		gpuName: z
			.string()
			.max(128)
			.optional()
			.describe(
				"GPU to pin, as the catalog's gpuName (e.g., RTX-4060); omitted means any",
			),
		// Bounds come from the shared constants rather than being restated, which
		// is how this drifted to 100 while the rest of the system said 135.
		browserVersion: z
			.number()
			.int()
			.min(BROWSER_VERSION_MIN)
			.max(BROWSER_VERSION_MAX)
			.describe("Browser version"),
		proxy: SaasProfileProxySchema.optional().describe("Proxy configuration"),
		notes: z.string().max(1000).optional().describe("Profile notes"),
		profileType: z
			.enum(["persistent", "one-time"])
			.default("persistent")
			.describe("persistent (kept) or one-time (auto-deleted after use)"),
		settings: ProfileSettingsSchema.optional().describe(
			"BotBrowser settings layered over the profile",
		),
		cookies: CookiesSchema.optional().describe(
			"Cookies loaded at launch (url or domain required per cookie)",
		),
	})
	.describe("Profile spec (creation / launch criteria)");

export const ProfileSpecSchema =
	ProfileSpecBaseSchema.superRefine(refineSettingsForOs);

export type ProfileSpec = z.infer<typeof ProfileSpecSchema>;

const CreateSaasProfileBaseSchema = ProfileSpecBaseSchema.extend({
	name: z.string().min(1).max(100).describe("Profile name"),
});

/** Create profile request (web) — same spec, name required. */
export const CreateSaasProfileSchema = CreateSaasProfileBaseSchema.superRefine(
	refineSettingsForOs,
).describe("Create profile request");

/**
 * Update profile request. Absent fields are left alone; `null` clears
 * `osVersion`, `gpuName`, `proxy`, `settings`, and `cookies` (`model` also
 * clears with ""). `settings` and `cookies` replace the stored value whole,
 * they are not merged. The OS check here only sees the patch, so the server
 * re-runs `validateSettingsForOs` on the merged profile.
 */
export const UpdateSaasProfileSchema = CreateSaasProfileBaseSchema.partial()
	.extend({
		// No default here: an update that leaves the type out must not reset it.
		profileType: z.enum(["persistent", "one-time"]).optional(),
		osVersion: z.string().max(32).nullable().optional(),
		model: z.string().max(64).nullable().optional(),
		gpuName: z.string().max(128).nullable().optional(),
		proxy: SaasProfileProxySchema.nullable().optional(),
		settings: ProfileSettingsSchema.nullable().optional(),
		cookies: CookiesSchema.nullable().optional(),
	})
	.superRefine(refineSettingsForOs)
	.describe("Update profile request");

/** Profile response schema */
export const SaasProfileResponseSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		organizationId: z.string(),
		os: z.string(),
		osVersion: z.string().nullable(),
		browserVersion: z.number(),
		model: z.string().nullable().optional(),
		gpuName: z.string().nullable().optional(),
		userAgent: z.string().nullable(),
		proxy: SaasProfileProxySchema.nullable(),
		notes: z.string().nullable(),
		status: z.enum(["idle", "running"]),
		profileType: z.enum(["persistent", "one-time"]),
		launchCount: z.number(),
		lastUsedAt: z.string().nullable(),
		createdAt: z.string(),
		updatedAt: z.string(),
		createdById: z.string(),
		settings: ProfileSettingsSchema.nullable().describe(
			"BotBrowser settings; null when the profile uses its defaults",
		),
		cookieCount: z
			.number()
			.int()
			.min(0)
			.describe("Stored cookies; the list itself is only on GET /:id"),
		proxyCheck: ProxyCheckResultSchema.nullable().describe(
			"Last cloud check of the stored proxy. Null when unchecked, or when the proxy changed since.",
		),
		stopRequestedAt: z
			.string()
			.nullable()
			.describe(
				"When a stop was asked for a running profile whose worker has not closed it yet. Null otherwise.",
			),
		browserState: ProfileBrowserStateSchema.nullable().describe(
			"Browser data saved from the last session. Null until a session saves some, or after a reset.",
		),
	})
	.describe("Profile response");

/** `GET /v1/profiles/:id`: the list response plus the stored cookies. */
export const SaasProfileDetailResponseSchema = SaasProfileResponseSchema.extend(
	{
		cookies: CookiesSchema.nullable(),
	},
).describe("Profile detail response");

/** Create / update response: the detail plus an advisory launchability hint. */
export const SaasProfileMutationResponseSchema =
	SaasProfileDetailResponseSchema.extend({
		launchable: LaunchableHintSchema.optional(),
	}).describe("Profile create/update response");

/** `GET /v1/profiles/catalog` */
export const ProfileCatalogResponseSchema = SuccessResponseSchema(
	ProfileCatalogSchema,
).describe("Profile catalog response");

export type ProfileCatalogResponse = z.infer<
	typeof ProfileCatalogResponseSchema
>;

/**
 * Columns a cloud profile listing may be sorted by. This reaches the ORM as an
 * `orderBy` key, so it is an explicit allowlist rather than a free string.
 */
export const SaasProfileSortFieldSchema = z
	.enum([
		"createdAt",
		"updatedAt",
		"name",
		"lastUsedAt",
		"launchCount",
		"status",
		"os",
		"osVersion",
		"browserVersion",
	])
	.describe("Profile field to sort by");

export type SaasProfileSortField = z.infer<typeof SaasProfileSortFieldSchema>;

/** Profile query with pagination and filters */
export const SaasProfilesQuerySchema = PaginationQuerySchema.extend({
	search: z.string().max(200).optional().describe("Search by name"),
	os: csvEnum(OsSchema).describe("Comma-separated operating systems"),
	status: csvEnum(z.enum(["idle", "running"])).describe(
		"Comma-separated statuses",
	),
	sortBy: SaasProfileSortFieldSchema.default("createdAt"),
}).describe("Profile query parameters");

/** Profile ID params */
export const ProfileIdParamsSchema = z.object({
	id: z.string().min(1).max(64).describe("Profile ID"),
});

/**
 * Params for the worker-facing session-tracking routes. `sessionId` is the
 * local-api session UUID, not a database id.
 */
export const ProfileSessionParamsSchema = z.object({
	id: z.string().min(1).max(64).describe("Profile ID"),
	sessionId: z.string().min(1).max(128).describe("Local API session ID"),
});

/** `POST /v1/profiles/:id/stop` */
export const StopProfileResponseSchema = z
	.object({
		id: z.string(),
		stopRequestedAt: z
			.string()
			.nullable()
			.describe(
				"When the stop was handed to the worker queue. Null when no browser was attached and the profile was set idle at once.",
			),
	})
	.describe("Stop profile response");

/** Bulk delete profiles request */
export const BulkDeleteProfilesSchema = z
	.object({
		ids: z.array(z.string()).min(1).describe("Profile IDs to delete"),
	})
	.describe("Bulk delete profiles request");

/** Dashboard stats response */
export const DashboardStatsResponseSchema = z
	.object({
		totalProfiles: z.number(),
		activeProfiles: z.number(),
		profileLimit: z.number(),
	})
	.describe("Dashboard statistics");

// ============ Profile Management Schemas (Local API Integration) ============

/** Profile type enum */
export const ProfileTypeSchema = z
	.enum(["persistent", "one-time"])
	.default("persistent")
	.describe(
		"Profile type - persistent profiles are kept, one-time profiles are auto-deleted after use",
	);

/** Extended fingerprint for profile configuration */
export const ExtendedFingerprintSchema = z
	.object({
		os: OsSchema.describe("Operating system"),
		osVersion: z.string().optional().describe("OS version (e.g., 10, 11, 14)"),
		browserVersion: z
			.number()
			.int()
			.min(100)
			.describe("Chrome browser version"),
		userAgent: z.string().optional().describe("Custom user agent string"),
		// Carried through to profile resolution so a chosen Android device is
		// honoured instead of picking any android bundle at random.
		model: z.string().optional().describe("Device model (Android only)"),
		gpuName: z
			.string()
			.optional()
			.describe("GPU to resolve against (local index gpuName)"),
		canvas: z.any().optional().describe("Canvas fingerprint configuration"),
		webgl: z.any().optional().describe("WebGL fingerprint configuration"),
		fonts: z.array(z.string()).optional().describe("Custom font list"),
	})
	.describe("Extended fingerprint configuration");

/** Profile config response (for Local API) */
/**
 * Resolved fingerprint attributes — the "sticky pin" recorded after a launch so
 * a persistent profile re-resolves to the same local `.enc` on later launches.
 * Not a file path: `.enc` files are worker-local and rotate, so we pin portable
 * attributes and degrade gracefully when the exact file is gone.
 */
export const ResolvedFingerprintSchema = z
	.object({
		hash: z.string().optional().describe("Profile file hash"),
		renderer: z.string().optional().describe("Unmasked GPU/WebGL renderer"),
		osVersion: z.string().optional().describe("Resolved OS version"),
		arch: z.string().optional().describe("Architecture (x64, arm64, ...)"),
		model: z.string().optional().describe("Device model (Android)"),
	})
	.describe("Resolved fingerprint pin");

export type ResolvedFingerprint = z.infer<typeof ResolvedFingerprintSchema>;

export const ProfileConfigSchema = z
	.object({
		id: z.string().describe("Profile ID"),
		name: z.string().describe("Profile name"),
		fingerprint: ExtendedFingerprintSchema.describe(
			"Fingerprint configuration",
		),
		proxy: SaasProfileProxySchema.nullable().describe("Proxy configuration"),
		profileType: ProfileTypeSchema.describe("Profile type"),
		pin: ResolvedFingerprintSchema.nullish().describe(
			"Sticky pin from a previous launch (persistent profiles)",
		),
		isRunning: z.boolean().describe("Whether profile is currently running"),
		settings: ProfileSettingsSchema.nullish().describe(
			"BotBrowser settings, for buildBotBrowserArgs",
		),
		cookies: CookiesSchema.nullish().describe(
			"Cookies to write for --bot-cookies (run normalizeCookies first)",
		),
		stateSync: z
			.boolean()
			.optional()
			.describe(
				"Save the browser state when the session ends. Absent from clouds without state storage.",
			),
		state: ProfileStateDownloadSchema.nullish().describe(
			"Browser state to restore before launch. Null when none is saved yet.",
		),
		cookiesPending: z
			.boolean()
			.optional()
			.describe(
				"`cookies` changed since the saved state was taken, so load them over it",
			),
	})
	.describe("Profile configuration for Local API");

/** Profile config response type */
export type ProfileConfig = z.infer<typeof ProfileConfigSchema>;

/** Extended profile response with new fields */
export const ExtendedSaasProfileResponseSchema =
	SaasProfileResponseSchema.extend({
		profileType: ProfileTypeSchema,
		fingerprint: z
			.any()
			.nullable()
			.describe("Extended fingerprint configuration"),
		launchCount: z
			.number()
			.describe("Number of times profile has been launched"),
		deletedAt: z
			.string()
			.nullable()
			.describe("Deletion timestamp for soft-deleted profiles"),
	}).describe("Extended profile response");

// ============ Session Tracking Schemas (SaaS API) ============

/** Start session request (from Local API) */
export const StartProfileSessionSchema = z
	.object({
		sessionId: z.string().describe("Local API session UUID"),
		machineId: z.string().optional().describe("Machine hostname/identifier"),
		resolved: ResolvedFingerprintSchema.optional().describe(
			"Resolved fingerprint attributes the worker actually launched (for the sticky pin)",
		),
	})
	.describe("Start profile session request");

/** Profile session response */
export const ProfileSessionResponseSchema = z
	.object({
		id: z.string().describe("Session record ID"),
		profileId: z.string().describe("Profile ID"),
		localSessionId: z.string().describe("Local API session UUID"),
		machineId: z.string().nullable().describe("Machine identifier"),
		status: z
			.enum(["running", "stopped", "crashed"])
			.describe("Session status"),
		startedAt: z.string().describe("Session start timestamp"),
		endedAt: z.string().nullable().describe("Session end timestamp"),
	})
	.describe("Profile session response");

/** Profile session type */
export type ProfileSession = z.infer<typeof ProfileSessionResponseSchema>;

/** Session list item (org-scoped, enriched with the profile name) */
export const SessionListItemSchema = ProfileSessionResponseSchema.extend({
	profileName: z.string().describe("Profile display name"),
}).describe("Session list item");

export type SessionListItem = z.infer<typeof SessionListItemSchema>;

/** Org session-list query (saas-api) */
export const SessionListQuerySchema = z
	.object({
		status: z
			.enum(["running", "stopped", "crashed"])
			.optional()
			.describe("Filter by status"),
		limit: z.coerce.number().int().min(1).max(200).optional(),
	})
	.describe("Session list query parameters");

// ============ Licensing Schemas (Local API -> SaaS API) ============

/** Launch authorization request (local-api -> saas-api, X-API-Key auth) */
export const LicenseAuthorizeSchema = z
	.object({
		profileId: z
			.string()
			.optional()
			.describe("Profile to launch; when set the config is returned inline"),
		// One-time by default here, unlike `POST /v1/profiles`, where the caller is
		// explicitly adding a profile to their library and persistent is right.
		// A launch-by-criteria caller is describing a browser it wants now, and
		// `ProfileSpecSchema` defaulting to persistent meant every such launch
		// added a permanent profile: on a three-profile plan, three ad-hoc
		// launches exhausted the quota for good, and the worker's own key has no
		// route that can delete a profile to recover. The field's description here
		// has said "default one-time" since it was written; the default did not.
		spec: ProfileSpecBaseSchema.extend({
			profileType: ProfileTypeSchema.default("one-time"),
		})
			.superRefine(refineSettingsForOs)
			.optional()
			.describe(
				"Launch-by-criteria: create a profile from this spec (default one-time) when no profileId is given",
			),
		os: OsSchema.optional().describe("Requested OS"),
		browserVersion: z
			.number()
			.int()
			.optional()
			.describe("Requested browser major version"),
		machineId: z.string().optional().describe("Worker machine identifier"),
	})
	.describe("Launch authorization request");

export type LicenseAuthorizeRequest = z.infer<typeof LicenseAuthorizeSchema>;

/** Session response with profile info */
export const SessionWithProfileResponseSchema = SessionResponseSchema.extend({
	profileId: z
		.string()
		.optional()
		.describe("Profile UUID if launched with profileId"),
	profileName: z.string().optional().describe("Profile name"),
	profileType: ProfileTypeSchema.optional().describe("Profile type"),
}).describe("Session response with profile information");

/** Session with profile type */
export type SessionWithProfileResponse = z.infer<
	typeof SessionWithProfileResponseSchema
>;

// ============ API Key Verification Schemas ============

/** API key verification request */
export const VerifyApiKeySchema = z
	.object({
		key: z.string().min(1).describe("API key to verify"),
	})
	.describe("API key verification request");

/** Rate limit info */
export const RateLimitInfoSchema = z
	.object({
		remaining: z.number().describe("Remaining requests"),
		resetAt: z.string().describe("Rate limit reset time (ISO 8601)"),
	})
	.describe("Rate limit information");

/** Organization limits info */
export const OrganizationLimitsSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		profileLimit: z
			.number()
			.describe("Total profile limit (base + additional)"),
		memberLimit: z.number().describe("Total member limit (base + additional)"),
		apiRpmLimit: z
			.number()
			.nullable()
			.describe("API requests per minute limit"),
		apiRphLimit: z.number().nullable().describe("API requests per hour limit"),
	})
	.describe("Organization limits");

/** API key verification response */
export const VerifyApiKeyResponseSchema = z
	.object({
		valid: z.boolean(),
		userId: z.string().optional(),
		userName: z.string().optional(),
		userEmail: z.string().optional(),
		organization: OrganizationLimitsSchema.optional(),
		rateLimit: RateLimitInfoSchema.optional(),
	})
	.describe("API key verification response");

/** API key verification response type */
export type VerifyApiKeyResponse = z.infer<typeof VerifyApiKeyResponseSchema>;

// ============ Prisma JSON Types Schemas ============
// These schemas define the structure of JSON fields in the database
// Used by prisma-json-types-generator for type safety

/** Plan features configuration stored as JSON in database */
export const PlanFeaturesSchema = z
	.object({
		teamCollaboration: z.boolean().optional(),
		apiAccess: z.boolean().optional(),
		customBranding: z.boolean().optional(),
		prioritySupport: z.boolean().optional(),
		advancedFingerprints: z.boolean().optional(),
		profileSharing: z.boolean().optional(),
		webhooks: z.boolean().optional(),
		sso: z.boolean().optional(),
		auditLogs: z.boolean().optional(),
	})
	.passthrough() // Allow additional boolean flags
	.describe("Plan features configuration");

/** Extended fingerprint configuration for profiles */
export const ProfileFingerprintSchema = z
	.object({
		canvas: z
			.object({
				noise: z.number().optional(),
				mode: z.enum(["noise", "block", "real"]).optional(),
			})
			.optional(),
		webgl: z
			.object({
				vendor: z.string().optional(),
				renderer: z.string().optional(),
				noise: z.number().optional(),
			})
			.optional(),
		fonts: z.array(z.string()).optional(),
		model: z.string().optional(),
		gpuName: z.string().optional(),
		screen: z
			.object({
				width: z.number().optional(),
				height: z.number().optional(),
				colorDepth: z.number().optional(),
			})
			.optional(),
		timezone: z.string().optional(),
		locale: z.string().optional(),
		webrtc: z
			.object({
				mode: z.enum(["disabled", "altered", "real"]).optional(),
				publicIp: z.string().optional(),
				localIps: z.array(z.string()).optional(),
			})
			.optional(),
		audio: z
			.object({
				noise: z.number().optional(),
			})
			.optional(),
	})
	.passthrough() // Allow additional fingerprint properties
	.describe("Extended fingerprint configuration for browser profiles");

/** Profile proxy configuration (stored as JSON) */
export const ProfileProxySchema = z
	.object({
		type: z.enum(["http", "https", "socks5", "socks5h"]),
		host: z.string(),
		port: z.number().int().min(1).max(65535),
		username: z.string().optional(),
		password: z.string().optional(),
	})
	.describe("Profile proxy configuration");

/** Plan snapshot stored in subscription (for grandfathering) */
export const PlanSnapshotSchema = z
	.object({
		name: z.string(),
		slug: z.string(),
		type: z.string(),
		description: z.string().nullable().optional(),
		priceMonthly: z.number().nullable().optional(),
		priceYearly: z.number().nullable().optional(),
		features: PlanFeaturesSchema.nullable().optional(),
		limits: z.object({
			profiles: z.number(),
			members: z.number(),
			apiRpm: z.number().nullable(),
			apiRph: z.number().nullable(),
		}),
		addonPricing: z.object({
			profile: z.number().nullable(),
			member: z.number().nullable(),
			rpm: z.number().nullable(),
		}),
		snapshotAt: z.string(), // ISO date string
	})
	.describe("Plan configuration snapshot at subscription creation time");

// ============ Prisma JSON Types ============
// Inferred types for use with prisma-json-types-generator

export type PlanFeatures = z.infer<typeof PlanFeaturesSchema>;
export type ProfileFingerprint = z.infer<typeof ProfileFingerprintSchema>;
export type ProfileProxy = z.infer<typeof ProfileProxySchema>;
export type PlanSnapshot = z.infer<typeof PlanSnapshotSchema>;

// ============ Profile Search Query Schemas ============

/**
 * Profile search/list query schema with pagination
 *
 * Uses flat schema for clean OpenAPI JSON Schema output.
 * Runtime refinement enforces: windowsVersion only valid when os='win' or undefined.
 */
export const ProfileSearchQuerySchema = PaginationQuerySchema.extend({
	filename: z.string().optional().describe("Exact filename to search for"),
	browserVersion: BrowserVersionSchema.optional().describe(
		"Filter by browser version",
	),
	os: OsSchema.optional().describe("Filter by OS (win, mac, linux, android)"),
	osVersion: z
		.string()
		.optional()
		.describe(
			"Filter by OS version (supports semver matching: '15' matches 15.x.x)",
		),
	windowsVersion: WindowsVersionSchema.optional().describe(
		"Filter by Windows version (7, 10, or 11). Only valid when os='win' or not specified.",
	),
	osArch: OsArchSchema.optional().describe("Filter by OS architecture"),
	renderer: z.string().optional().describe("GPU renderer (partial match)"),
	model: z
		.union([z.string(), z.array(z.string())])
		.optional()
		.describe("Device model filter — single value or array (Android only)"),
	gpuName: z.string().optional().describe("Filter by GPU name"),
})
	.refine(
		(data) => {
			// windowsVersion is only valid for Windows or when os is not specified
			if (data.windowsVersion && data.os && data.os !== "win") {
				return false;
			}
			return true;
		},
		{
			message: "windowsVersion is only valid when os is 'win' or not specified",
			path: ["windowsVersion"],
		},
	)
	.describe("Profile search/list query parameters with pagination");

export type ProfileSearchQuery = z.infer<typeof ProfileSearchQuerySchema>;

/** Type guard for Windows profile query (os is 'win' or undefined) */
export function isWindowsProfileQuery(query: ProfileSearchQuery): boolean {
	return query.os === "win" || query.os === undefined;
}

/** Profile search response schema with pagination */
export const ProfileSearchResponseSchema = SuccessResponseSchema(
	PaginatedResponseSchema(LocalProfileMetadataSchema).describe(
		"Profile search response with pagination",
	),
);

export type ProfileSearchResponse = z.infer<typeof ProfileSearchResponseSchema>;

// ============ Chromium Catalog Schemas (SaaS upstream proxy) ============

/**
 * A single neutral Chromium release entry. The cloud API resolves these from
 * its upstream source and returns only neutral fields (no upstream repo or
 * vendor naming). The self-hosted control plane mirrors them into its
 * `ChromiumUpstreamRelease` table.
 */
export const ChromiumCatalogReleaseSchema = z
	.object({
		tagName: z.string().describe("Release tag (e.g. v147.0.7727.139)"),
		fullVersion: z
			.string()
			.describe("Full chromium version (e.g. 147.0.7727.139)"),
		browserMajor: z.number().int().describe("Major version component (147)"),
		assetName: z
			.string()
			.nullable()
			.describe("Neutral downloadable asset name (null when no x64 build)"),
		assetSizeBytes: z
			.number()
			.int()
			.nullable()
			.describe("Asset size in bytes when known"),
		publishedAt: z.string().describe("ISO 8601 publish timestamp"),
		hasX64Deb: z
			.boolean()
			.describe("Whether an installable x64 build exists for this release"),
	})
	.describe("Neutral Chromium catalog release entry");

export type ChromiumCatalogRelease = z.infer<
	typeof ChromiumCatalogReleaseSchema
>;

/** Chromium catalog list response (cloud API `GET /v1/chromium/releases`). */
export const ChromiumCatalogResponseSchema = SuccessResponseSchema(
	z.object({
		releases: z.array(ChromiumCatalogReleaseSchema),
	}),
).describe("Chromium catalog response");

export type ChromiumCatalogResponse = z.infer<
	typeof ChromiumCatalogResponseSchema
>;

// ============ Contact / Sales enquiry (SaaS API) ============

/** Team size buckets for the public contact form */
export const ContactTeamSizeSchema = z
	.enum(["1-10", "11-50", "51-200", "200+"])
	.describe("Team size range");

/** Public contact / sales enquiry request */
export const ContactFormSchema = z
	.object({
		name: z.string().trim().min(1).max(100).describe("Full name"),
		email: z.string().trim().email().max(254).describe("Work email"),
		company: z
			.string()
			.trim()
			.max(200)
			.optional()
			.or(z.literal(""))
			.describe("Company name"),
		teamSize: ContactTeamSizeSchema,
		message: z.string().trim().min(10).max(5000).describe("Enquiry message"),
		/** reCAPTCHA v3 token; required by the API in production */
		captchaToken: z.string().min(1).optional().describe("reCAPTCHA token"),
	})
	.describe("Contact / sales enquiry request");

export type ContactFormInput = z.infer<typeof ContactFormSchema>;

// ============ Auth & Account Form Schemas (web dashboard) ============
//
// These schemas are the single source of truth for the dashboard/marketing
// forms. Each exposes a `build<Name>Schema(messages)` factory so components can
// inject i18n (next-intl) messages while keeping validation rules here, plus a
// default schema (English messages) used to infer the form value types.

/** Password must contain at least one lowercase, one uppercase, and one digit. */
export const PASSWORD_COMPLEXITY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

/** Organization name allows letters, numbers, spaces, hyphens, and underscores. */
export const ORGANIZATION_NAME_REGEX = /^[a-zA-Z0-9\s\-_]+$/;

/** Value that must be typed to confirm irreversible account deletion. */
export const ACCOUNT_DELETE_CONFIRMATION = "DELETE";

// ---- Login ----

export interface LoginMessages {
	emailRequired?: string;
	emailInvalid?: string;
	passwordRequired?: string;
}

export function buildLoginSchema(messages: LoginMessages = {}) {
	return z.object({
		email: z
			.string()
			.trim()
			.min(1, messages.emailRequired ?? "Email is required")
			.email(messages.emailInvalid ?? "Enter a valid email address"),
		password: z
			.string()
			.min(1, messages.passwordRequired ?? "Password is required"),
	});
}

export const LoginSchema = buildLoginSchema().describe("Login request");
export type LoginInput = z.infer<typeof LoginSchema>;

// ---- Signup ----

export interface SignupMessages {
	nameRequired?: string;
	nameTooLong?: string;
	emailRequired?: string;
	emailInvalid?: string;
	passwordMin?: string;
}

export function buildSignupSchema(messages: SignupMessages = {}) {
	return z.object({
		name: z
			.string()
			.trim()
			.min(1, messages.nameRequired ?? "Name is required")
			.max(100, messages.nameTooLong ?? "Name must be 100 characters or less"),
		email: z
			.string()
			.trim()
			.min(1, messages.emailRequired ?? "Email is required")
			.email(messages.emailInvalid ?? "Enter a valid email address"),
		password: z
			.string()
			.min(8, messages.passwordMin ?? "Password must be at least 8 characters"),
	});
}

export const SignupSchema = buildSignupSchema().describe("Signup request");
export type SignupInput = z.infer<typeof SignupSchema>;

// ---- Forgot password ----

export interface ForgotPasswordMessages {
	emailRequired?: string;
	emailInvalid?: string;
}

export function buildForgotPasswordSchema(
	messages: ForgotPasswordMessages = {},
) {
	return z.object({
		email: z
			.string()
			.trim()
			.min(1, messages.emailRequired ?? "Email is required")
			.email(messages.emailInvalid ?? "Enter a valid email address"),
	});
}

export const ForgotPasswordSchema = buildForgotPasswordSchema().describe(
	"Forgot password request",
);
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

// ---- Change password ----

export interface ChangePasswordMessages {
	currentRequired?: string;
	newMin?: string;
	newComplexity?: string;
	confirmRequired?: string;
	mismatch?: string;
}

export function buildChangePasswordSchema(
	messages: ChangePasswordMessages = {},
) {
	return z
		.object({
			currentPassword: z
				.string()
				.min(1, messages.currentRequired ?? "Current password is required"),
			newPassword: z
				.string()
				.min(8, messages.newMin ?? "Password must be at least 8 characters")
				.regex(
					PASSWORD_COMPLEXITY_REGEX,
					messages.newComplexity ??
						"Password must include uppercase, lowercase, and a number",
				),
			confirmPassword: z
				.string()
				.min(1, messages.confirmRequired ?? "Please confirm your password"),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: messages.mismatch ?? "Passwords do not match",
			path: ["confirmPassword"],
		});
}

export const ChangePasswordSchema = buildChangePasswordSchema().describe(
	"Change password request",
);
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;

// ---- Reset password ----

export interface ResetPasswordMessages {
	newMin?: string;
	newComplexity?: string;
	confirmRequired?: string;
	mismatch?: string;
}

/**
 * Same strength rules as changing a password, minus the current one: the reset
 * link is the proof of identity here.
 */
export function buildResetPasswordSchema(messages: ResetPasswordMessages = {}) {
	return z
		.object({
			newPassword: z
				.string()
				.min(8, messages.newMin ?? "Password must be at least 8 characters")
				.regex(
					PASSWORD_COMPLEXITY_REGEX,
					messages.newComplexity ??
						"Password must include uppercase, lowercase, and a number",
				),
			confirmPassword: z
				.string()
				.min(1, messages.confirmRequired ?? "Please confirm your password"),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: messages.mismatch ?? "Passwords do not match",
			path: ["confirmPassword"],
		});
}

export const ResetPasswordSchema = buildResetPasswordSchema().describe(
	"Reset password request",
);
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

// ---- Delete account ----

export interface DeleteAccountMessages {
	passwordRequired?: string;
	confirmationInvalid?: string;
}

export function buildDeleteAccountSchema(messages: DeleteAccountMessages = {}) {
	return z.object({
		password: z
			.string()
			.min(1, messages.passwordRequired ?? "Password is required"),
		confirmation: z
			.string()
			.refine((val) => val === ACCOUNT_DELETE_CONFIRMATION, {
				message:
					messages.confirmationInvalid ??
					`Please type "${ACCOUNT_DELETE_CONFIRMATION}" to confirm`,
			}),
	});
}

export const DeleteAccountSchema = buildDeleteAccountSchema().describe(
	"Delete account request",
);
/**
 * Output type — `confirmation` is narrowed to the literal `"DELETE"` by the
 * refinement, so this represents post-validation values.
 */
export type DeleteAccountInput = z.output<typeof DeleteAccountSchema>;
/** Input type used for the form's field values (before refinement narrowing). */
export type DeleteAccountFormInput = z.input<typeof DeleteAccountSchema>;

// ---- Update profile name ----

export interface UpdateProfileNameMessages {
	nameRequired?: string;
	nameTooLong?: string;
}

export function buildUpdateProfileNameSchema(
	messages: UpdateProfileNameMessages = {},
) {
	return z.object({
		name: z
			.string()
			.trim()
			.min(1, messages.nameRequired ?? "Name is required")
			.max(100, messages.nameTooLong ?? "Name must be 100 characters or less"),
	});
}

export const UpdateProfileNameSchema = buildUpdateProfileNameSchema().describe(
	"Update profile name request",
);
export type UpdateProfileNameInput = z.infer<typeof UpdateProfileNameSchema>;

// ---- Organization name (creation wizard / settings) ----

export interface OrganizationNameMessages {
	min?: string;
	max?: string;
	pattern?: string;
}

export function buildOrganizationNameSchema(
	messages: OrganizationNameMessages = {},
) {
	return z.object({
		name: z
			.string()
			.trim()
			.min(2, messages.min ?? "Organization name must be at least 2 characters")
			.max(
				50,
				messages.max ?? "Organization name must be less than 50 characters",
			)
			.regex(
				ORGANIZATION_NAME_REGEX,
				messages.pattern ??
					"Only letters, numbers, spaces, hyphens, and underscores allowed",
			),
	});
}

export const OrganizationNameSchema =
	buildOrganizationNameSchema().describe("Organization name");
export type OrganizationNameInput = z.infer<typeof OrganizationNameSchema>;

// ---- Create API key ----

export interface CreateApiKeyMessages {
	nameRequired?: string;
	nameTooLong?: string;
}

export function buildCreateApiKeySchema(messages: CreateApiKeyMessages = {}) {
	return z.object({
		name: z
			.string()
			.trim()
			.min(1, messages.nameRequired ?? "Please enter a name for the API key")
			.max(100, messages.nameTooLong ?? "Name must be 100 characters or less"),
	});
}

export const CreateApiKeySchema = buildCreateApiKeySchema().describe(
	"Create API key request",
);
export type CreateApiKeyInput = z.infer<typeof CreateApiKeySchema>;

/** API key form where the name is optional (e.g. organization worker keys). */
export const CreateApiKeyOptionalNameSchema = z
	.object({
		name: z
			.string()
			.trim()
			.max(100, "Name must be 100 characters or less")
			.optional(),
	})
	.describe("Create API key request (optional name)");
export type CreateApiKeyOptionalNameInput = z.infer<
	typeof CreateApiKeyOptionalNameSchema
>;

// ---- Profile create/edit form (web) ----
// Lives in `profile-form.ts` with its mappers to and from the API shape.
